"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { countSyllables } from "@/lib/tool-kit/style-checkers/syllables";

const WORD_BREAKDOWN_LIMIT = 40;

// Module-level regexes (Biome: regexes must not live inside functions/loops).
const RE_NON_ALPHA = /[^a-z]/g;
const RE_WORD_SPLIT = /\s+/;
const RE_NEWLINE = /\n/;

function wordsOf(text: string): string[] {
  return text
    .trim()
    .split(RE_WORD_SPLIT)
    .filter((w) => w.toLowerCase().replace(RE_NON_ALPHA, "").length > 0);
}

interface LineCount {
  index: number;
  text: string;
  syllables: number;
}

interface WordCount {
  word: string;
  syllables: number;
}

function analyzeWords(text: string): WordCount[] {
  return wordsOf(text)
    .map((word) => ({ word, syllables: countSyllables(word) }))
    .filter((w) => w.syllables > 0);
}

function analyzeLines(text: string): LineCount[] {
  return text
    .split(RE_NEWLINE)
    .map((line, index) => ({
      index: index + 1,
      text: line.trim(),
      syllables: analyzeWords(line).reduce((sum, w) => sum + w.syllables, 0),
    }))
    .filter((line) => line.text.length > 0);
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const SYLLABLE_COUNTER_STRINGS = {
  inputLabel: "Your text — one line per verse for per-line counts",
  placeholder:
    "An old silent pond\nA frog jumps into the pond—\nSplash! Silence again.",
  clear: "Clear",
  syllables: "Syllables",
  words: "Words",
  avgSyllablesPerWord: "Avg syllables / word",
  syllablesPerLine: "Syllables per line",
  haikuHint:
    "Checking a haiku? The classic pattern is 5-7-5 syllables across three lines.",
  syllablesPerWord: "Syllables per word",
} as const;

export default function SyllableCounterWidget() {
  const [text, setText] = useState("");
  const strings = useToolUi(SYLLABLE_COUNTER_STRINGS);
  const fmt = useToolNumber();
  const hasInput = text.trim().length > 0;

  const words = analyzeWords(text);
  const lines = analyzeLines(text);
  const totalSyllables = words.reduce((sum, w) => sum + w.syllables, 0);
  const avgSyllables =
    words.length > 0
      ? Math.round((totalSyllables / words.length) * 100) / 100
      : 0;

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="syllable-input"
        >
          {strings.inputLabel}
        </label>
        <textarea
          className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="syllable-input"
          onChange={(e) => setText(e.target.value)}
          placeholder={strings.placeholder}
          value={text}
        />
        {hasInput ? (
          <button
            className="rounded-md border border-border bg-background px-3 py-1.5 font-medium text-foreground text-sm hover:bg-muted"
            onClick={() => setText("")}
            type="button"
          >
            {strings.clear}
          </button>
        ) : null}
      </div>

      {/* Stats */}
      {hasInput ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.syllables} value={fmt(totalSyllables)} />
          <StatCard label={strings.words} value={fmt(words.length)} />
          <StatCard
            label={strings.avgSyllablesPerWord}
            value={fmt(avgSyllables, { maximumFractionDigits: 2 })}
          />
        </div>
      ) : null}

      {/* Per-line counts */}
      {hasInput && lines.length > 1 ? (
        <section aria-live="polite" className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.syllablesPerLine}
          </h2>
          <p className="text-muted-foreground text-sm">{strings.haikuHint}</p>
          <div className="space-y-2">
            {lines.map((line) => (
              <div
                className="flex items-baseline justify-between gap-4 rounded-lg border border-border bg-card px-4 py-3"
                key={line.index}
              >
                <p className="min-w-0 truncate text-foreground text-sm">
                  <span className="mr-2 text-muted-foreground text-xs">
                    {line.index}.
                  </span>
                  {line.text}
                </p>
                <p className="shrink-0 font-semibold text-foreground text-sm">
                  {line.syllables}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* Per-word breakdown */}
      {hasInput && words.length > 0 && words.length <= WORD_BREAKDOWN_LIMIT ? (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.syllablesPerWord}
          </h2>
          <div className="flex flex-wrap gap-2">
            {words.map((entry, index) => (
              <span
                className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm"
                key={`${entry.word}-${index}`}
              >
                <span className="text-foreground">{entry.word}</span>
                <span className="ml-2 font-semibold text-primary">
                  {entry.syllables}
                </span>
              </span>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}
